import { useState } from "react";
import { Check, X } from "@phosphor-icons/react";
import { api, act, useFetch, fmtDate } from "@/lib/api";
import { PageHeader, Badge, Empty } from "@/components/common";
import { Sel } from "@/components/Sel";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FixGuide } from "@/components/FixGuide";

const Item = ({ e, onDone }) => {
  const editable = e.type === "reply_draft";
  const [text, setText] = useState(e.payload?.draft || "");
  const [busy, setBusy] = useState(false);
  const resolve = async (decision) => {
    setBusy(true);
    const body = { decision };
    if (editable && decision === "approve" && text !== e.payload?.draft) body.text = text;
    const r = await act(() => api.post(`/exceptions/${e.id}/resolve`, body), decision === "approve" ? "Approved" : "Rejected");
    setBusy(false);
    if (r) { window.dispatchEvent(new Event("exceptions-changed")); onDone(); }
  };
  const overdue = e.deadline < new Date().toISOString();
  return (
    <div className="bg-[#121212] border border-border rounded-md p-5 rise" data-testid={`exception-${e.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge v={e.severity} /><Badge v={e.type} tone="blue" /><h3 className="font-semibold text-base font-sans">{e.title}</h3>
        <span className={`ml-auto text-[11px] font-mono ${overdue ? "text-[#FF3B30]" : "text-zinc-500"}`}>owner {e.owner} · due {fmtDate(e.deadline)}</span>
      </div>
      <div className="grid md:grid-cols-3 gap-4 mt-4 text-sm">
        <div><div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Why</div><div className="text-zinc-300">{e.reason}</div></div>
        <div><div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Evidence</div><div className="text-zinc-400 text-xs whitespace-pre-wrap max-h-28 overflow-y-auto">{e.evidence || "—"}</div></div>
        <div><div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Recommendation</div>
          {editable ? <Textarea data-testid={`exception-edit-${e.id}`} rows={5} value={text} onChange={(x) => setText(x.target.value)} className="bg-black text-xs" /> : <div className="text-zinc-300 text-xs whitespace-pre-wrap">{e.recommendation}</div>}
        </div>
      </div>
      {e.guide && <FixGuide id={e.id} guide={e.guide} />}
      {e.status === "open" ? (
        <div className="flex gap-2 mt-4 justify-end">
          <Button size="sm" variant="secondary" disabled={busy} data-testid={`exception-reject-${e.id}`} onClick={() => resolve("reject")} className="gap-1"><X /> Reject</Button>
          <Button size="sm" disabled={busy} data-testid={`exception-approve-${e.id}`} onClick={() => resolve("approve")} className="gap-1 bg-[#00C781] hover:bg-[#00C781]/80 text-black"><Check /> {editable ? "Approve & send" : "Approve"}</Button>
        </div>
      ) : <div className="mt-3 text-xs text-zinc-500">Resolved: <Badge v={e.status} /> by {e.resolved_by || "timeout"} {fmtDate(e.resolved_at)}</div>}
    </div>
  );
};

export default function Exceptions() {
  const [status, setStatus] = useState("open");
  const { data, reload } = useFetch(`/exceptions?status=${status}`);
  return (
    <div data-testid="exceptions-page">
      <PageHeader eyebrow="AIO05 · Exception-only workspace" title="Your decisions." desc="Only what needs judgement. Silence is never approval — expired items keep dependent work paused while independent compliant work continues.">
        <Sel testid="exceptions-status-filter" className="w-40" value={status} onChange={setStatus} options={["open", "approved", "edited", "rejected", "expired", "all"]} />
      </PageHeader>
      {!data?.length ? <Empty>Nothing here.</Empty> : <div className="space-y-3">{data.map((e) => <Item key={e.id} e={e} onDone={reload} />)}</div>}
    </div>
  );
}
